import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, inject } from '@angular/core';
import { AuthService } from '../../../core/services/auth/auth.service';
import { Router, RouterLink } from '@angular/router';
import { LocalStorageService } from '../../../core/services/localStorage/local-storage.service';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { NotificationService } from '../../../core/services/notification/notification.service';
import { UsersService } from '../../../core/services/users/users.service';
import { OrdersService } from '../../../core/services/orders/orders.service';
import { Subscription } from 'rxjs';
import { HeaFilterComponent } from "./hea-filter/hea-filter.component";
import { SearchOverlayComponent } from "./search-overlay/search-overlay.component";
import { AuthStateService } from '../../../core/services/auth-state/auth-state-service.service';
import { SessionManagerService } from '../../../core/services/session-manager/session-manager.service';
import { StoredSession } from '../../../core/services/session-manager/session.model';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreakpointObserver } from '@angular/cdk/layout';
import { KeyboardShortcutService } from '../../../core/services/keyboard/keyboard-shortcut.service';
import { VisibilityPollingService } from '../../../core/services/visibility-polling/visibility-polling.service';
import { KeyboardCheatsheetComponent } from '../../shared/keyboard-cheatsheet/keyboard-cheatsheet.component';
import { ReloginModalComponent } from './relogin-modal/relogin-modal.component';
import Swal from 'sweetalert2';
import { AuthResponse } from '../../../core/services/auth/auth.service';
import { MAX_SESSIONS } from '../../../core/services/session-manager/session.model';

interface Notification {
  reference: string;
  type: string;
  title: string;
  content: string;
  url: string;
  expiration_date: string;
  read_status: boolean;
}

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    RouterLink, CommonModule, TranslateModule, HeaFilterComponent,
    MatTooltipModule, SearchOverlayComponent, KeyboardCheatsheetComponent,
    ReloginModalComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Output() toggleSidebar = new EventEmitter<void>();

  isMobile = false;
  rightDrawerOpen = false;
  searchOverlayOpen = false;
  cheatsheetVisible = false;
  drawerNotificationsExpanded = false;
  drawerLanguageExpanded = false;

  private keyboardService = inject(KeyboardShortcutService);
  private unregisterShortcuts: (() => void) | null = null;

  role: any = 'admin';
  environment = environment.production;
  showNotifications = false;
  notifications: Notification[] = [];
  unreadCount: number = 0;
  showNotes = false;
  userData: any;
  userImageUrl: string | null = null;
  actionRequiredCount = 0;
  privateActionRequiredCount = 0;
  actionRequiredCountAgent = 0;
  public selectedLanguage: any = {
    language: 'Spanish',
    code: 'es',
    icon: 'es',
    flag: 'https://flagcdn.com/w40/es.png',
  }

  public languages: any[] = [
    {
      language: 'español',
      code: 'es',
      icon: 'es',
      flag: 'https://flagcdn.com/w40/es.png',
    },
    {
      language: 'italiano',
      code: 'it',
      icon: 'it',

      flag: 'https://flagcdn.com/w40/it.png',
    }, {
      language: 'ingles',
      code: 'en',
      icon: 'en',
      flag: 'https://flagcdn.com/w40/us.png',
    },
    {
      language: 'frances',
      code: 'fr',
      icon: 'fr',
      flag: 'https://flagcdn.com/w40/fr.png',
    }
  ]

  // Multi-account state
  sessions: StoredSession[] = [];
  activeSessionIndex = 0;
  activeSessionName = '';
  activeSessionAccount = '';
  showAddAccountModal = false;
  reloginSession: StoredSession | null = null;

  constructor(
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
    private authStateService: AuthStateService,
    private sessionManager: SessionManagerService,
    private usersService: UsersService,
    private orderService: OrdersService,
    private translate: TranslateService,
    private localStore: LocalStorageService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private visibilityPolling: VisibilityPollingService
  ) {
    var code = this.localStore.getLanguage() || 'es';
    translate.setDefaultLang(code);
    this.selectedLanguage = this.languages.find((l) => l.code === code);
    this.role = this.authStateService.getRole();
    this.showNotes = this.role != 'Workshop';
  }
  private subscriptions = new Subscription();

  ngOnInit(): void {
    // Multi-account: subscribe to session changes
    this.subscriptions.add(
      this.sessionManager.sessions$.subscribe(sessions => {
        this.sessions = sessions;
      })
    );
    this.subscriptions.add(
      this.sessionManager.activeIndex$.subscribe(index => {
        this.activeSessionIndex = index;
        const active = this.sessions[index];
        this.activeSessionName = active?.name || '';
        this.activeSessionAccount = active?.account_name || '';
      })
    );

    this.subscriptions.add(
      this.breakpointObserver.observe('(max-width: 992px)').subscribe(result => {
        this.isMobile = result.matches;
        if (!this.isMobile) {
          this.rightDrawerOpen = false;
        }
      })
    );
    this.orderService.initializeActionRequiredAutoUpdate();
    this.fetchUserData();
    this.orderService.actionRequiredCount$.subscribe((count) => {
      this.actionRequiredCount = count;
    });
  this.orderService.actionRequiredCountAgent$.subscribe((count) => {
      this.actionRequiredCountAgent = count;
    });

    this.orderService.privateActionRequiredCount$.subscribe((count) => {
      this.privateActionRequiredCount = count;
    });

    this.getNumber();
    this.fetchAllData();
    this.visibilityPolling.register('header-notifications', () => this.fetchAllData(), 60000);

    this.usersService.userImage$.subscribe((imageUrl) => {
      this.userImageUrl = imageUrl;
    });

    this.registerKeyboardShortcuts();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.unregisterShortcuts?.();
    this.visibilityPolling.unregister('header-notifications');
  }

  private registerKeyboardShortcuts(): void {
    this.unregisterShortcuts = this.keyboardService.registerMany([
      {
        key: 'k', ctrl: true, allowInInput: true,
        handler: () => { this.openSearchOverlay(); },
        description: 'search', category: 'global', priority: 5,
      },
      {
        key: '?', allowInInput: false,
        handler: () => { this.cheatsheetVisible = !this.cheatsheetVisible; },
        description: 'cheatsheet', category: 'global', priority: 10,
      },
      {
        key: 'Escape', allowInInput: true,
        handler: () => {
          if (this.cheatsheetVisible) { this.cheatsheetVisible = false; return; }
          if (this.searchOverlayOpen) { this.closeSearchOverlay(); return; }
          return false; // Not handled — let other handlers try
        },
        priority: 10,
      },
    ]);
  }

  changeLanguage(lang: any) {
    this.translate.use(lang.code)
    this.localStore.setLanguage(lang.code)
    this.selectedLanguage = this.languages.find((l) => l.code === lang.code);
  }

  private fetchAllData() {
    Promise.all([this.fetchData()]).then(() => {
      this.unreadCount = this.notificationService.countUnreadNotifications(
        this.notifications
      );
    });
  }

  fetchData(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.notificationService.getData().subscribe({
        next: (data) => {
          this.notifications = data.data;
          this.unreadCount = this.notificationService.countUnreadNotifications(
            this.notifications
          );
        },
        error: (error) => {
          reject(error);
        },
      });
    });
  }

  markAsRead(reference: string): void {
    this.notificationService.updateStatus(reference, { read: true }).subscribe(
      (response) => { },
      (error) => { }
    );
    this.notificationService.markAsRead(reference);
    this.unreadCount = this.notificationService.countUnreadNotifications(
      this.notifications
    );
  }

  isRead(reference: string): boolean {
    const aux = this.notifications.find((n) => n.reference === reference);
    if (aux?.read_status) {
      return true;
    }
    // Verifica si la notificación está en las cookies o si su read_status es 1
    return this.notificationService.isRead(reference);
  }

  toggleNotificationsMenu() {
    this.showNotifications = !this.showNotifications;
  }

  logout() {
    this.authService.logout();
  }

  // --- Multi-account ---

  onSwitchAccount(index: number): void {
    if (index === this.activeSessionIndex) return;
    const success = this.sessionManager.switchToSession(index);
    if (!success) {
      this.reloginSession = this.sessions[index];
    } else {
      this.refreshHeaderState();
      this.router.navigateByUrl(this.authStateService.getRedirectUrl());
    }
  }

  async onAddAccount(): Promise<void> {
    if (this.sessionManager.getSessionCount() >= MAX_SESSIONS) {
      Swal.fire({
        icon: 'warning',
        title: this.translate.instant('multi_account.max_sessions_reached'),
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // Step 1: Email + Password
    const loginResult = await Swal.fire({
      title: this.translate.instant('multi_account.add_account_title'),
      html: `
        <p class="text-muted mb-3">${this.translate.instant('multi_account.add_account_subtitle')}</p>
        <div class="mb-3 text-start">
          <label class="form-label">${this.translate.instant('login.email')}</label>
          <input id="swal-email" type="email" class="swal2-input" placeholder="email@example.com" style="margin:0;width:100%" />
        </div>
        <div class="mb-3 text-start">
          <label class="form-label">${this.translate.instant('login.password')}</label>
          <input id="swal-password" type="password" class="swal2-input" style="margin:0;width:100%" />
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: this.translate.instant('multi_account.relogin_btn'),
      cancelButtonText: this.translate.instant('multi_account.cancel'),
      confirmButtonColor: '#3085d6',
      focusConfirm: false,
      preConfirm: () => {
        const email = (document.getElementById('swal-email') as HTMLInputElement)?.value;
        const password = (document.getElementById('swal-password') as HTMLInputElement)?.value;
        if (!email || !password) {
          Swal.showValidationMessage(this.translate.instant('swal_messages.login.please_complete_form'));
          return false;
        }
        return { email, password };
      },
    });

    if (!loginResult.isConfirmed || !loginResult.value) return;

    const { email, password } = loginResult.value;

    // Call login in add mode
    Swal.fire({ title: this.translate.instant('swal_messages.loading'), allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const response = await this.authService.login({ email, password }, 'add').toPromise() as AuthResponse;
      Swal.close();

      if (this.authService.isMfaRequired(response)) {
        await this.handleAddAccountMfa(response.data!.challenge_token!, email);
      } else {
        this.authService.handleSuccessfulLogin(response, 'add', email);
        this.refreshHeaderState();
        Swal.fire({ icon: 'success', title: this.translate.instant('multi_account.add_account_title'), timer: 1500, showConfirmButton: false });
      }
    } catch {
      Swal.fire({ icon: 'error', title: this.translate.instant('swal_messages.login.login_failed'), confirmButtonColor: '#d33' });
    }
  }

  private async handleAddAccountMfa(challengeToken: string, email: string): Promise<void> {
    const mfaResult = await Swal.fire({
      title: this.translate.instant('login.mfa.code_label'),
      html: `
        <input id="swal-mfa-code" type="text" class="swal2-input" maxlength="6" placeholder="000000" style="text-align:center;letter-spacing:8px;font-size:1.5rem" />
        <label class="form-check mt-2" style="display:flex;align-items:center;gap:6px;justify-content:center">
          <input id="swal-remember" type="checkbox" class="form-check-input" />
          <span class="form-check-label">${this.translate.instant('login.mfa.remember_device')}</span>
        </label>
      `,
      showCancelButton: true,
      confirmButtonText: this.translate.instant('multi_account.relogin_btn'),
      cancelButtonText: this.translate.instant('multi_account.cancel'),
      confirmButtonColor: '#3085d6',
      focusConfirm: false,
      preConfirm: () => {
        const code = (document.getElementById('swal-mfa-code') as HTMLInputElement)?.value;
        if (!code || code.length !== 6) {
          Swal.showValidationMessage(this.translate.instant('swal_messages.mfa.mfa_code_invalid'));
          return false;
        }
        const remember = (document.getElementById('swal-remember') as HTMLInputElement)?.checked;
        return { code, remember };
      },
    });

    if (!mfaResult.isConfirmed || !mfaResult.value) return;

    Swal.fire({ title: this.translate.instant('swal_messages.loading'), allowOutsideClick: false, didOpen: () => Swal.showLoading() });

    try {
      const response = await this.authService.verifyMfa({
        challenge_token: challengeToken,
        code: mfaResult.value.code,
        remember_device: mfaResult.value.remember || undefined,
        device_label: mfaResult.value.remember ? navigator.userAgent.substring(0, 255) : undefined,
      }).toPromise() as AuthResponse;

      Swal.close();
      this.authService.handleSuccessfulLogin(response, 'add', email);
      this.refreshHeaderState();
      Swal.fire({ icon: 'success', title: this.translate.instant('multi_account.add_account_title'), timer: 1500, showConfirmButton: false });
    } catch {
      Swal.fire({ icon: 'error', title: this.translate.instant('swal_messages.mfa.mfa_code_invalid'), confirmButtonColor: '#d33' });
    }
  }

  onReloginSuccess(index: number): void {
    this.reloginSession = null;
    this.sessionManager.switchToSession(index);
    this.refreshHeaderState();
    this.router.navigateByUrl(this.authStateService.getRedirectUrl());
  }

  private refreshHeaderState(): void {
    this.role = this.authStateService.getRole();
    this.showNotes = this.role !== 'Workshop';
    this.fetchUserData();
    this.getNumber();
    this.fetchAllData();
  }

  fetchUserData(): void {
    this.usersService.getUserByReference().subscribe(
      (response) => {
        this.userData = response.data;
        this.userImageUrl = this.userData.img;
        if (this.userData.img) {
          this.sessionManager.updateActiveSessionImg(this.userData.img);
        }
      },
      (error) => {
        console.error('Error loading user data:', error);
      }
    );
  }
  private linkElement?: HTMLLinkElement;
  changeTheme(theme: string) {
    this.linkElement = document.createElement('link');
    this.linkElement.rel = 'stylesheet';
    document.head.appendChild(this.linkElement);
    document.body.classList.remove(
      'theme-blue',
      'theme-brown',
      'theme-green',
      'theme-orange'
    );

    // Agrega la nueva clase de tema si existe
    if (theme) {
      document.body.classList.add(theme);
    }
  }
  openRightDrawer(): void {
    if (this.isMobile) {
      this.rightDrawerOpen = true;
      this.drawerNotificationsExpanded = false;
      this.drawerLanguageExpanded = false;
    }
  }

  closeRightDrawer(): void {
    this.rightDrawerOpen = false;
  }

  onDrawerNavigate(): void {
    this.closeRightDrawer();
  }

  onDrawerLanguageChange(lang: any): void {
    this.changeLanguage(lang);
    this.drawerLanguageExpanded = false;
  }

  openSearchOverlay(): void {
    this.searchOverlayOpen = true;
  }

  closeSearchOverlay(): void {
    this.searchOverlayOpen = false;
  }

  count: number = 0;
  getNumber() {
    this.orderService.getActionRequiredCount().subscribe({
      next: (response) => {
        this.actionRequiredCount = response.data?.inbox_action_requireds;
        this.actionRequiredCountAgent = response.data?.agent_inbox_action_requireds;
      },
      error: (err) => {
        console.error('Error al obtener la cantidad de notas:', err);
      },
    });
   
    this.orderService.getPrivateActionRequiredCount().subscribe({
      next: (response) => {
        this.privateActionRequiredCount = response.data?.inbox_private_action_requireds;
      },
      error: (err) => {
        console.error('Error al obtener la cantidad de notas privadas:', err);
      },
    });
  }



}
